import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import './style/ProductDetail.css'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'

const grindLabel = options => {
  if (options.mahlgrad !== 'gemahlen') return 'Ganze Bohne'
  const brew =
    options.kaffeeart === 'siebträger'
      ? 'Siebträger'
      : options.kaffeeart === 'filter'
        ? 'Filter'
        : 'Espresso'
  return `Gemahlen, ${brew}`
}

const ProductDetail = ({ produkte, abwesend }) => {
  const { id } = useParams() // Holt sich die ID aus der URL
  const navigate = useNavigate()
  const produkt = produkte.find(p => p.id === id) // Produkt anhand der ID finden
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentFullSizeViewImage, setCurrentFullSizeViewImage] = useState(null)
  const [cartNotice, setCartNotice] = useState(null)
  const confirmPrimaryRef = useRef(null)

  const [selectedOptions, setSelectedOptions] = useState({
    kaffeeart: 'siebträger',
    mahlgrad: 'ganze Bohne',
    menge: '1000g',
    stueckzahl: 1
  })

  // Preis dynamisch berechnen
  const selectedMengeNumber = parseInt(
    selectedOptions?.menge.replace('g', ''),
    10
  )

  let aufpreis = 0
  if (selectedMengeNumber === 250) aufpreis = 1.5
  else if (selectedMengeNumber === 500) aufpreis = 1.0

  const dynamischerPreis = (
    (produkt?.price / 1000) * selectedMengeNumber +
    aufpreis
  ).toFixed(2)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (!cartNotice) return
    confirmPrimaryRef.current?.focus()
    const onKey = event => {
      if (event.key === 'Escape') setCartNotice(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [cartNotice])

  const handleAddToCart = () => {
    if (abwesend) {
      alert('Der Shop ist zurzeit geschlossen. Bitte versuche es später erneut.')
      return
    }
    const stueckzahl = Math.min(
      99,
      Math.max(1, Math.round(Number(selectedOptions.stueckzahl)) || 1)
    )
    const options = { ...selectedOptions, stueckzahl }
    const cart = JSON.parse(localStorage.getItem('cart')) || []

    // Produkt mit exakt denselben Optionen finden
    const existingItem = cart.find(
      item =>
        item.id === produkt.id &&
        item.selectedOptions.kaffeeart === options.kaffeeart &&
        item.selectedOptions.mahlgrad === options.mahlgrad &&
        item.selectedOptions.menge === options.menge
    )

    if (existingItem) {
      // Falls das Produkt existiert, die Stückzahl erhöhen
      existingItem.selectedOptions.stueckzahl = Math.min(
        99,
        Number(existingItem.selectedOptions.stueckzahl) + stueckzahl
      )
    } else {
      // Neues Produkt in den Warenkorb hinzufügen
      cart.push({ ...produkt, selectedOptions: options })
    }

    // **WICHTIG: Den aktualisierten Warenkorb im LocalStorage speichern**
    localStorage.setItem('cart', JSON.stringify(cart))

    setCartNotice({
      name: produkt.name,
      summary: `${stueckzahl}× ${options.menge} · ${grindLabel(options)}`
    })
  }

  if (!produkt) {
    return <h2>Produkt nicht gefunden</h2>
  }

  return (
    <div className='productDetailContainer'>
      {cartNotice && (
        <div
          className='cartConfirmationMessage'
          onClick={() => setCartNotice(null)}
        >
          <div
            className='cartConfirmationMessageInnerDiv'
            role='dialog'
            aria-modal='true'
            aria-labelledby='cart-notice-title'
            onClick={event => event.stopPropagation()}
          >
            <CheckCircleOutlineIcon className='cartConfirmIcon' />
            <h2 id='cart-notice-title'>Im Warenkorb</h2>
            <p>
              <strong>{cartNotice.name}</strong> wurde hinzugefügt.
            </p>
            <p className='cartConfirmSummary'>{cartNotice.summary}</p>
            <div className='cartConfirmActions'>
              <button
                ref={confirmPrimaryRef}
                className='cartConfirmPrimary'
                onClick={() => navigate('/warenkorb')}
              >
                Zum Warenkorb
              </button>
              <button
                className='cartConfirmSecondary'
                onClick={() => setCartNotice(null)}
              >
                Weiter einkaufen
              </button>
            </div>
          </div>
        </div>
      )}
      {currentFullSizeViewImage !== null && (
        <div className='fullSizeImageContainer'>
          <div className='arrowContainer'>
            {currentFullSizeViewImage > 0 && (
              <button
                className='arrowButton'
                style={{ left: '25px' }}
                onClick={() =>
                  setCurrentFullSizeViewImage(currentFullSizeViewImage - 1)
                }
              >
                <ArrowBackIcon />
              </button>
            )}
            {currentFullSizeViewImage < produkt.images.length - 1 && (
              <button
                className='arrowButton'
                style={{ right: '25px' }}
                onClick={() =>
                  setCurrentFullSizeViewImage(currentFullSizeViewImage + 1)
                }
              >
                <ArrowForwardIcon />
              </button>
            )}
          </div>
          <img
            src={produkt.images[currentFullSizeViewImage]}
            alt={produkt.name}
            onClick={() => setCurrentFullSizeViewImage(null)}
          />
        </div>
      )}
      <div className='productImageContainer'>
        {/* 🔹 Hauptbild */}
        <div className='productThumbnaileImageContainer'>
          {produkt.images && produkt.images.length > 0 && (
            <img
              src={produkt.images[currentImageIndex]}
              alt={produkt.name}
              className='productMainImage'
              onClick={() => setCurrentFullSizeViewImage(currentImageIndex)}
            />
          )}
        </div>

        {/* 🔹 Thumbnail-Leiste */}
        <div className='productMainImageContainer'>
          {produkt.images &&
            produkt.images.length > 1 &&
            produkt.images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={produkt.name}
                className='thumbnailImage'
                onClick={() => setCurrentImageIndex(index)} // Klick aktualisiert das Hauptbild
                style={
                  currentImageIndex === index
                    ? { border: '3px solid #8b4513' }
                    : {}
                }
              />
            ))}
        </div>
      </div>

      {/* 🔹 Produkt-Details */}
      <div className='productDetailInfoContainer'>
        <h1>{produkt.name}</h1>
        <p className='productDescription'>{produkt.description}</p>
        <p className='productPrice'>
          {dynamischerPreis} €{' '}
          <span
            style={{ color: 'gray', fontSize: '0.9rem', fontWeight: 'lighter' }}
          >
            ({produkt.price} / kg)
          </span>
        </p>

        {/* 🔹 Auswahlfelder */}

        <div className='product-detail-container'>
          <div className='select-group'>
            <label>Mahlgrad</label>
            <select
              value={selectedOptions.mahlgrad}
              onChange={e =>
                setSelectedOptions({
                  ...selectedOptions,
                  mahlgrad: e.target.value
                })
              }
            >
              <option value='ganze Bohne'>Ganze Bohne</option>
              <option value='gemahlen'>Gemahlen</option>
            </select>
          </div>
          {selectedOptions.mahlgrad === 'gemahlen' && (
            <div className='select-group' style={{ marginBottom: '20px' }}>
              <label>Kaffeeart</label>
              <select
                value={selectedOptions.kaffeeart}
                onChange={e =>
                  setSelectedOptions({
                    ...selectedOptions,
                    kaffeeart: e.target.value
                  })
                }
              >
                <option value='siebträger'>Siebträger</option>
                <option value='filter'>Filter</option>
                <option value='espresso'>Espresso</option>
              </select>
            </div>
          )}
          <div className='select-group'>
            <label>Menge</label>
            <select
              value={selectedOptions.menge}
              onChange={e =>
                setSelectedOptions({
                  ...selectedOptions,
                  menge: e.target.value
                })
              }
            >
              <option value='250g' id='oidascheisdreck'>
                <strong id='mannnnnichdrehdurchhier'>250</strong>g
              </option>
              <option value='500g'>
                <strong>500</strong>g
              </option>
              <option value='1000g'>
                <strong>1000</strong>g
              </option>
            </select>
          </div>
          <div className='select-group'>
            <label>Stückzahl</label>
            <input
              type='number'
              value={selectedOptions.stueckzahl}
              onChange={e =>
                setSelectedOptions({
                  ...selectedOptions,
                  stueckzahl: Number(e.target.value)
                })
              }
              min='1'
            />
          </div>

          {/* 🔹 In den Warenkorb Button */}
          <button
            className='product-detail-button'
            onClick={handleAddToCart}
            disabled={abwesend}
          >
            {abwesend ? 'Zurzeit geschlossen' : 'In den Warenkorb'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ProductDetail
